/**
 * A Scrollspy class that tracks section visibility in the viewport and updates the associated
 * navigation links dynamically by applying an active class.
 */
export class Scrollspy {
    private readonly sections: HTMLElement[];
    private readonly observer: IntersectionObserver | undefined;
    private readonly navLinks: NodeListOf<HTMLAnchorElement>;
    private readonly activeClass: string;

    /**
     * Constructs a ScrollSpy instance to manage and observe sections and navigation links for visibility and active states.
     *
     * @param {string} [sectionSelector='[id^="product_"][data-scrollspy]'] - The selector used to locate the sections to observe.
     * @param {string} [navSelector='.product-detail__jumpTo a'] - The selector used to locate the navigation links associated with the sections.
     * @param {string} [activeClass='active'] - The CSS class to apply to the active navigation link when its corresponding section is in view.
     */
    constructor(
        sectionSelector: string = '[id^="product_"][data-scrollspy]',
        navSelector: string = '.product-detail__jumpTo a',
        activeClass: string = 'active'
    ) {
        this.sections = Array.from(document.querySelectorAll(sectionSelector));
        this.navLinks = document.querySelectorAll(navSelector);
        this.activeClass = activeClass;

        if (this.sections.length === 0 || this.navLinks.length === 0) {
            return;
        }

        const observerOptions: IntersectionObserverInit = {
            root: null,
            rootMargin: '0px',
            threshold: 0.5,
        };

        this.observer = new IntersectionObserver(this.handleIntersection.bind(this), observerOptions);
        this.sections.forEach((section) => this.observer?.observe(section));
    }

    /**
     * Handles the intersection events from the IntersectionObserver to determine which elements
     * are currently visible in the viewport and updates the active link accordingly.
     *
     * @param {IntersectionObserverEntry[]} entries - An array of IntersectionObserverEntry objects representing the intersection changes.
     * @return {void} This method does not return a value.
     */
    private handleIntersection(entries: IntersectionObserverEntry[]): void {
        const visibleEntries = entries.filter((entry) => entry.isIntersecting);

        if (visibleEntries.length > 0) {
            const mostVisibleEntry = visibleEntries.reduce((prev, current) =>
                prev.intersectionRatio > current.intersectionRatio ? prev : current
            );

            this.updateActiveLink(mostVisibleEntry.target.id);
        }
    }

    /**
     * Updates the active link in the navigation by adding or removing an active class
     * and optionally scrolling the active element into view.
     *
     * @param {string} activeId - The identifier of the currently active section or element.
     * @return {void} This method does not return a value.
     */
    private updateActiveLink(activeId: string): void {
        this.navLinks.forEach((link) => {
            if (link.hash === `#${activeId}`) {
                link.classList.add(this.activeClass);
                this.scrollActiveElementIntoView(link);
            } else {
                link.classList.remove(this.activeClass);
            }
        });
    }

    /**
     * Scrolls the specified element into view within its container.
     *
     * This method calculates the appropriate scroll position for the element
     * to ensure it is centered horizontally inside the container with smooth scrolling.
     *
     * @param {HTMLElement} element - The DOM element to scroll into view. It must be a child of the container element.
     * @return {void} This method does not return any value.
     */
    private scrollActiveElementIntoView(element: HTMLElement): void {
        const container = document.getElementById('product_jumpto');

        if (!container) {
            return;
        }

        const containerRect = container?.getBoundingClientRect();
        const elementRect = element?.getBoundingClientRect();
        const scrollLeft = element.offsetLeft - containerRect.width / 2 + elementRect.width / 2;

        container?.scrollTo({
            left: scrollLeft,
            behavior: 'smooth',
        });
    }

    /**
     * Disconnects the observer if it exists. This method is used to stop observing changes
     * or interactions that the observer was monitoring.
     *
     * @return {void} No value is returned by this method.
     */
    public disconnect(): void {
        if (this.observer) {
            this.observer.disconnect();
        }
    }
}
